import Permission from './permission.enum'

/*
  PATHS is the central place to define all application routes, titles, and permissions.
  * The root-level `url` properties start with `/` to indicate full navigation to a page (e.g., `/vessel-visits`).
  * Nested children omit the leading `/` so their paths are appended to the parent route (e.g., `/vessel-visits/1290/crane-split`). 
*/
export const PATHS = {
  dashboard: {
    navId: 'dashboard',
    url: '/dashboard',
    title: 'dashboard',
  },
  issues: {
    navId: 'issues',
    url: '/issues',
    title: 'issues',
    issues: {
      navId: 'issues',
      url: '/issues',
      title: 'issues',
      permission: Permission.ReadDamageCatalogue,
    },
    holds: {
      navId: 'holds',
      url: '/holds',
      title: 'holds',
      // TODO: add permission
    },
  },

  visits: {
    navId: 'visits',
    title: 'visits',
    vesselPlanning: {
      url: '/vessel-visits-planning',
      title: 'vesselVisitsPlanning',
      permission: Permission.ReadVessels,
    },
    vesselVisits: {
      navId: 'vesselVisits',
      url: '/vessel-visits',
      title: 'vesselVisits',
      permission: Permission.ReadVisits,
      craneSplit: {
        url: 'crane-split',
        title: 'craneSplit',
        permission: Permission.ReadJobs,
      },
      equipments: {
        url: 'equipments',
        title: 'equipments',
        permission: Permission.EquipmentsAssignment,
      },
      kpi: {
        url: 'kpi',
        title: 'kpi',
        permission: Permission.ReadJobs,
      },
      stoppages: {
        url: 'stoppages',
        title: 'stoppages',
        permission: Permission.ReadStoppageReasons,
      },
      hatchCovers: {
        url: 'hatch-covers',
        title: 'hatchCovers',
        permission: Permission.ReadVessels,
      },
      load: {
        url: 'load',
        title: 'load',
        permission: Permission.ReadOrders,
      },
      discharge: {
        url: 'discharge',
        title: 'discharge',
        permission: Permission.ReadOrders,
      },
      restow: {
        url: 'restow',
        title: 'restow',
        permission: Permission.ReadOrders,
      },
      order: {
        url: 'order',
        title: 'order',
        permission: Permission.WriteOrders,
      },
    },
    railPlanning: {
      url: '/rail-visits-planning',
      title: 'railVisitsPlanning',
      permission: Permission.ReadRailVisits,
    },
    railVisits: {
      navId: 'railVisits',
      url: '/rail-visits',
      title: 'railVisits',
      permission: Permission.ReadRailVisits,
      equipments: {
        url: 'equipments',
        title: 'equipments',
        permission: Permission.EquipmentsAssignment,
      },
      order: {
        url: 'order',
        title: 'order',
        permission: Permission.WriteOrders,
      },
      checkin: {
        url: 'checkin',
        title: 'checkin',
        permission: Permission.WriteOrders,
      },
      checkout: {
        url: 'checkout',
        title: 'checkout',
        permission: Permission.WriteOrders,
      },
      load: {
        url: 'load',
        title: 'load',
        permission: Permission.ReadOrders,
      },
      discharge: {
        url: 'discharge',
        title: 'discharge',
        permission: Permission.ReadOrders,
      },
    },
    visits: {
      navId: 'visits',
      url: '/visits',
      title: 'visits',
      permission: Permission.ReadVisits,
    },
    truckVisits: {
      navId: 'truckVisits',
      url: '/truck-visits',
      title: 'truckVisits',
      permission: Permission.ReadVisits,
      new: {
        url: 'new',
        title: 'truckVisitsNew',
        permission: Permission.WriteOrders,
      },
    },
    containerVisits: {
      navId: 'containerVisits',
      url: '/container-visits',
      title: 'containerVisits',
      permission: Permission.ReadOrders,
      order: {
        url: 'order',
        title: 'order',
        permission: Permission.WriteOrders,
      },
      checkin: {
        url: 'checkin',
        title: 'checkin',
        permission: Permission.WriteOrders,
      },
      checkout: {
        url: 'checkout',
        title: 'checkout',
        permission: Permission.WriteOrders,
      },
    },
  },

  orders: {
    navId: 'orders',
    title: 'orders',
    numericReleases: {
      navId: 'numericReleases',
      url: '/numeric-releases',
      title: 'numericReleases',
      permission: Permission.ReadOrders,
    },
    nonNumericReleases: {
      navId: 'nonNumericReleases',
      url: '/non-numeric-releases',
      title: 'nonNumericReleases',
      permission: Permission.ReadOrders,
    },
    nnrForVisit: {
      navId: 'nnrForVisit',
      url: '/nnr-for-visit',
      title: 'nnrForVisit',
      permission: Permission.ReadOrders,
    },
    bookings: {
      navId: 'bookings',
      url: '/bookings',
      title: 'bookings',
      permission: Permission.ReadOrders,
      visit: {
        url: 'visit',
        title: 'visit',
        permission: Permission.ReadOrders,
      },
    },
    serviceOrders: {
      navId: 'serviceOrders',
      url: '/service',
      title: 'serviceOrders',
      permission: Permission.ReadOrders,
    },
  },

  controlRoom: {
    navId: 'controlRoom',
    title: 'controlRoom',
    equipments: {
      navId: 'equipments',
      url: '/equipments',
      title: 'equipments',
      permission: Permission.ReadEquipments,
    },
    operationalEquipment: {
      navId: 'operationalEquipment',
      url: '/operational-equipment',
      title: 'operationalEquipment',
      permission: Permission.ReadEquipments,
    },
    jobs: {
      navId: 'jobs',
      url: '/work-instructions',
      title: 'jobs',
      permission: Permission.ReadJobs,
    },
    reeferMonitoring: {
      navId: 'reeferMonitoring',
      url: '/reefer-monitoring',
      title: 'jobsReefer',
    },
  },

  yard: {
    navId: 'yard',
    title: 'yard',
    permission: Permission.ReadYardBlocks,
    view: {
      navId: 'yardView',
      url: '/yard-view',
      title: 'yard',
    },
    planningDashboard: {
      navId: 'yardPlanningDashboard',
      url: '/yard-planning-dashboard',
      title: 'yardPlanningDashboard',
    },
    planningIssues: {
      url: '/yard-planning-issues',
      title: 'yardPlanningIssues',
    },
    inconsistencies: {
      url: '/yard-inconsistencies',
      title: 'yardInconsistencies',
    },
    management: {
      url: '/yard-management',
      title: 'yardManagement',
    },
    containerTurnovers: {
      navId: 'containerTurnovers',
      url: '/container-turnovers',
      title: 'containerTurnovers',
    },
    globalAllocationRules: {
      url: '/global-allocation-rules',
      title: 'globalAllocationRules',
    },
  },

  operators: {
    navId: 'operators',
    title: 'operators',
    yardclerk: {
      navId: 'yardclerk',
      url: '/yardclerk',
      title: 'yardClerk',
      permission: Permission.ReadYardBlocks,
    },
    tallyclerk: {
      navId: 'tallyclerk',
      url: '/tallyclerk',
      title: 'tallyClerk',
      permission: Permission.ReadVessels,
    },
    gateClerk: {
      navId: 'gateClerk',
      url: '/gate-clerk',
      title: 'gateClerk',
      permission: Permission.ReadVisits,
    },
    crane: {
      navId: 'crane',
      url: '/crane',
      title: 'craneDesktop',
      permission: Permission.StartFinishJobs,
      bayView: {
        url: 'bay-view',
        title: 'craneOperatorBayView',
        permission: Permission.ReadJobs,
      },
      noGeometryView: {
        url: 'no-geometry-view',
        title: 'craneOperatorNoGeometryView',
        permission: Permission.ReadJobs,
      },
    },
    operator: {
      navId: 'operator',
      url: '/operator',
      title: 'operator',
      permission: Permission.StartFinishJobs,
    },
    all: {
      navId: 'operators-all',
      url: '/operator/all',
      title: 'allEquipments',
      permission: Permission.StartFinishJobs,
    },
    rs: {
      navId: 'operators-rs',
      url: '/operator/rs',
      title: 'RS',
      permission: Permission.StartFinishJobs,
    },
    ech: {
      navId: 'operators-ech',
      url: '/operator/ech',
      title: 'emptyHandlerRS',
      permission: Permission.StartFinishJobs,
    },
    rtg: {
      navId: 'operators-rtg',
      url: '/operator/rtg',
      title: 'RTG',
      permission: Permission.StartFinishJobs,
    },
    sts: {
      navId: 'operators-sts',
      url: '/operator/sts',
      title: 'craneMobile',
      permission: Permission.StartFinishJobs,
    },
    rmg: {
      navId: 'operators-rmg',
      url: '/operator/rmg',
      title: 'RMG',
      permission: Permission.StartFinishJobs,
    },
    tt: {
      navId: 'operators-tt',
      url: '/operator/tt',
      title: 'TT',
      permission: Permission.StartFinishJobs,
    },
    generalCargo: {
      navId: 'generalCargo',
      url: '/general-cargo-operator',
      title: 'generalCargoOperator',
      permission: Permission.StartFinishJobs,
    },
    reeferOperator: {
      navId: 'reeferOperator',
      url: '/reefer-operator',
      title: 'reeferOperator',
      permission: Permission.StartFinishJobs,
    },
    operatorContainerTurnoversList: {
      url: '/operator-container-turnovers-list',
      title: 'operatorContainerTurnoversList',
    },
  },

  generalCargo: {
    navId: 'generalCargo',
    title: 'generalCargo',
    orders: {
      navId: 'generalCargoOrders',
      url: '/general-cargo-orders',
      title: 'generalCargoOrders',
    },
    inventory: {
      navId: 'generalCargoInventory',
      url: '/general-cargo-inventory',
      title: 'generalCargoInventory',
    },
    cargoAreas: {
      navId: 'generalCargoAreas',
      url: '/general-cargo-areas',
      title: 'generalCargoAreas',
    },
  },

  masterData: {
    navId: 'masterData',
    url: '/master-data',
    title: 'masterData',
    permission: Permission.ReadMasterData,
    holdTypes: {
      navId: 'holdTypes',
      url: '/master-data/hold-types',
      title: 'holdTypes',
      permission: Permission.ReadMasterData,
      add: {
        url: '/master-data/hold-types/add',
        title: 'addHoldType',
        permission: Permission.ReadMasterData,
      },
      edit: {
        url: '/master-data/hold-types/:id/edit',
        title: 'editHoldType',
        permission: Permission.ReadMasterData,
      },
    },
    stowageInstruction: {
      navId: 'stowageInstructions',
      url: '/master-data/stowage-instructions',
      title: 'stowageInstructions',
      permission: Permission.ReadStowageInstructions,
    },
    archetypes: {
      navId: 'archetypes',
      url: '/master-data/archetypes',
      title: 'archetypes',
      permission: Permission.ReadOrders,
    },
    isoCodeMappings: {
      navId: 'isoCodeMappings',
      url: '/master-data/iso-code-mappings',
      title: 'isoCodeMappings',
      permission: Permission.ReadTenants,
    },
    locationCodes: {
      navId: 'locationCodes',
      url: '/master-data/location-codes',
      title: 'locationCodes',
      permission: Permission.ReadLocationCodes,
    },
    masterLocationCodes: {
      navId: 'masterLocationCodes',
      url: '/master-data/master-location-codes',
      title: 'masterLocationCodes',
      permission: Permission.ResetData,
    },
    stoppageReason: {
      navId: 'stoppageReason',
      url: '/master-data/stoppage-reason',
      title: 'stoppageReason',
      permission: Permission.ReadStoppageReasons,
    },
    commoditiesAndPackaging: {
      navId: 'commoditiesAndPackaging',
      url: '/master-data/commodities-and-packaging',
      title: 'commoditiesAndPackaging',
      permission: Permission.ReadCommodities,
      commodity: {
        url: 'commodity',
        title: 'commoditiesAndPackingCommodity',
        permission: Permission.WriteCommodities,
      },
    },
    allocationRuleTemplates: {
      navId: 'allocationRuleTemplates',
      url: '/master-data/allocation-rule-templates',
      title: 'allocationRuleTemplates',
      permission: Permission.ReadYardPlanning,
    },
    containerLengthBayMappings: {
      navId: 'containerLengthBayMappings',
      url: '/master-data/container-length-bay-mappings',
      title: 'containerLengthBayMappings',
      permission: Permission.ReadDangerousGoodsSettings,
    },
    weightClasses: {
      navId: 'weightClasses',
      url: '/master-data/weight-classes',
      title: 'weightClasses',
      permission: Permission.ReadWeightClasses,
    },
    damageCatalogue: {
      navId: 'damageCatalogue',
      url: '/master-data/damage-catalogue',
      title: 'damageCatalogue',
      permission: Permission.ReadDamageCatalogue,
    },
    dangerousGoodsSettings: {
      navId: 'dangerousGoodsSettings',
      url: '/master-data/dangerous-goods-settings',
      title: 'dangerousGoodsSettings',
    },
    vessels: {
      navId: 'vessels',
      url: '/master-data/vessels',
      title: 'vessels',
      permission: Permission.ReadVessels,
    },
    containers: {
      navId: 'containers',
      url: '/master-data/containers',
      title: 'containers',
      permission: Permission.ReadOrders,
      history: {
        url: 'history',
        title: 'containersHistory',
        permission: Permission.ReadOrders,
      },
    },
    railcars: {
      navId: 'railcars',
      url: '/master-data/railcars',
      title: 'railcars',
      permission: Permission.ReadVessels,
    },
    companies: {
      navId: 'companies',
      url: '/master-data/companies',
      title: 'companies',
      permission: Permission.ReadCompanies,
    },
    shift: {
      navId: 'shift',
      url: '/master-data/shift',
      title: 'shift',
      permission: Permission.ReadShift,
    },
    gang: {
      navId: 'gang',
      url: '/master-data/gang',
      title: 'gang',
      permission: Permission.ReadGangs,
    },
    vesselServices: {
      navId: 'vesselServices',
      url: '/master-data/vessel-services',
      title: 'vesselServices',
      permission: Permission.ReadVessels,
    },
    externalDrivers: {
      navId: 'externalDrivers',
      url: '/master-data/external-drivers',
      title: 'externalDrivers',
      permission: Permission.ReadWriteExternalDrivers,
    },
    externalDriversForm: {
      url: '/master-data/external-driver-form',
      title: 'externalDriverForm',
      permission: Permission.ReadWriteExternalDrivers,
    },
  },

  config: {
    navId: 'config',
    title: 'config',
    yardSetup: {
      navId: 'yardSetup',
      url: '/yard-setup',
      title: 'yardSetup',
      permission: Permission.ReadYardBlocks,
    },
    berths: {
      navId: 'berths',
      url: '/berths',
      title: 'berthsManagement',
      permission: Permission.ReadBerths,
    },
    piers: {
      navId: 'piers',
      url: '/piers',
      title: 'piersManagement',
      permission: Permission.ReadPiers,
    },
    railTracks: {
      navId: 'railTracks',
      url: '/rail-tracks',
      title: 'trackManagement',
    },
    users: {
      navId: 'users',
      url: '/users',
      title: 'users',
      permission: Permission.ReadUsers,
    },
    roles: {
      navId: 'roles',
      url: '/roles',
      title: 'roles',
      permission: Permission.ReadUsers,
    },
    tenants: {
      navId: 'tenants',
      url: '/tenants',
      title: 'tenants',
      permission: Permission.ReadAdminTenant,
    },
    tenantConfiguration: {
      navId: 'tenantConfiguration',
      url: 'tenant-configuration',
      title: 'tenantConfiguration',
      permission: Permission.ReadTenants,
    },
    reset: {
      navId: 'reset',
      url: 'reset',
      title: 'reset',
      permission: Permission.ResetData,
    },
    csvUpload: {
      navId: 'csvUpload',
      url: 'csv-upload',
      title: 'uploadOrders',
    },
  },

  reports: {
    navId: 'reports',
    url: '/reports',
    title: 'reports',
    permission: Permission.ReadBillableReport,
  },

  reportsV2: {
    navId: 'reports-v2',
    url: '/reports-v2',
    title: 'containerJourneyReports',

    containerJourneys: {
      navId: 'containers',
      url: '/reports-v2/container-journeys',
      title: 'containerJourneys',
    },
  },

  profile: {
    navId: 'profile',
    url: '/profile',
    title: 'profile',
  },

  portDefense: {
    navId: 'portDefense',
    url: '/port-defense',
    title: 'portDefense',
  },

  reachStackerRunner: {
    navId: 'reachStackerRunner',
    url: '/reach-stacker-runner',
    title: 'reachStackerRunner',
  },

  yardHaul: {
    navId: 'yardHaul',
    url: '/yard-haul',
    title: 'yardHaul',
  },

  yardPacman: {
    navId: 'yardPacman',
    url: '/yard-pacman',
    title: 'yardPacman',
  },

  vesselInvaders: {
    navId: 'vesselInvaders',
    url: '/vessel-invaders',
    title: 'vesselInvaders',
  },

  containerPinball: {
    navId: 'containerPinball',
    url: '/container-pinball',
    title: 'containerPinball',
  },

  donkeyStacker: {
    navId: 'donkeyStacker',
    url: '/donkey-stacker',
    title: 'donkeyStacker',
  },

  terminalCrossing: {
    navId: 'terminalCrossing',
    url: '/terminal-crossing',
    title: 'terminalCrossing',
  },

  inventoryDrone: {
    navId: 'inventoryDrone',
    url: '/inventory-drone',
    title: 'inventoryDrone',
  },

  arcade: {
    navId: 'arcade',
    url: '/arcade',
    title: 'arcade',
  },

  documentationStatus: {
    navId: 'documentationStatus',
    url: '/documentation-status',
    title: 'documentationStatus',
  },

  notFound: {
    url: '/404',
    title: 'notFound',
  },
}
